import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Label } from 'reactstrap';
import api from '../api';
import { adminTeamMembersActions } from '../redux/actions';
import useError from '../utils/HookErrorUtil';
import useToast from '../utils/HookToast';
import { CardLoader } from './Loader';
import RowContent from './RowContent';
import LabeledChoice, { eventToMultipleChoicesValue } from './LabeledChoice';
import Help from './Help';
import NewTooltip from './NewTooltip';
import InvitationTooltipBase from './InvitationTooltipBase';
import { InvitationAvatar, UserAvatar } from './avatar';
import TeamMemberPermissionSettings from './TeamMemberPermissionSettings';
import {
  CAN_CREATE_AND_ADMINISTRATE_PROJECTS,
  CAN_CREATE_AND_ADMINISTRATE_RESEARCH_TEAMS,
  CAN_MANAGE_PUBLIC_PROFILE,
  CAN_VIEW_PROJECTS,
  CAN_VIEW_RESEARCH_TEAMS,
  CAN_INVITE_TEAM_MEMBER,
  CAN_MANAGE_TEAM_PERMISSIONS,
  ADMIN_TEAM_PERMISSIONS,
  INSTITUTION_ONLY_PERMISSIONS,
  USER_TYPE_HEALTHCARE_FACILITY,
  LOCK_MODEL_TEAM_MEMBER,
} from '../constants';

/**
 * AdminMemberAvatar - Displays an avatar with tooltip for admin team members
 * Similar to MemberAvatar in TeamForm.jsx
 */
function AdminMemberAvatar({
  member,
  admin,
  onResendInvitation,
  onRemoveMember,
  onPermissionsChange,
  isHealthcareFacility,
  t,
}) {
  const [editing, setEditing] = useState(false);
  const [permissions, setPermissions] = useState(
    member.team_permissions ? member.team_permissions.map((p) => p.name) : [],
  );

  // Available permissions for admin teams
  const writablePermissions = isHealthcareFacility
    ? [
      CAN_VIEW_PROJECTS,
      CAN_CREATE_AND_ADMINISTRATE_PROJECTS,
      CAN_VIEW_RESEARCH_TEAMS,
      CAN_CREATE_AND_ADMINISTRATE_RESEARCH_TEAMS,
      CAN_MANAGE_PUBLIC_PROFILE,
      CAN_INVITE_TEAM_MEMBER,
      CAN_MANAGE_TEAM_PERMISSIONS,
    ]
    : [
      CAN_VIEW_PROJECTS,
      CAN_CREATE_AND_ADMINISTRATE_PROJECTS,
      CAN_MANAGE_PUBLIC_PROFILE,
      CAN_INVITE_TEAM_MEMBER,
      CAN_MANAGE_TEAM_PERMISSIONS,
    ];

  const handleEditionToggle = (isEditing) => {
    setEditing(isEditing);
    if (!isEditing && permissions !== member.team_permissions?.map((p) => p.name)) {
      // Reset permissions if cancel
      setPermissions(member.team_permissions ? member.team_permissions.map((p) => p.name) : []);
    }
  };

  const handlePermissionToggle = async (e) => {
    const { checked, value: permName } = e.target;
    const newPermissions = checked
      ? [...permissions, permName]
      : permissions.filter((p) => p !== permName);
    setPermissions(newPermissions);

    // Save immediately
    if (onPermissionsChange) {
      await onPermissionsChange(member, newPermissions);
    }
    return true; // Signal success to TeamMemberPermissionSettings
  };

  // member.user is the full user object from UserMemberSerializer (or null if not confirmed)
  const memberUser = member.user || { email: member.email };

  return (
    <div className="mr-2 mt-2 position-relative d-inline">
      <InvitationTooltipBase
        admin={admin}
        dataLockerType={LOCK_MODEL_TEAM_MEMBER}
        invitation={{
          ...member,
          user: memberUser,
        }}
        showManagement
        canRemove
        resendInvitation={() => onResendInvitation(member)}
        cancelInvitation={() => onRemoveMember(member.id)}
        removeGuest={() => onRemoveMember(member.id)}
        onEditionToggle={handleEditionToggle}
        removeGuestLabel="user:admin-team.member.remove"
        removeGuestWarning={(
          <span>
            {t('user:admin-team.member.remove-warning')}
            <br />
            <br />
            {t('common:confirm-deletion')}
          </span>
        )}
        extraContent={(
          <>
            <div className="font-italic mb-2">
              {member.confirmed ? member.email : t('user:team.invitations.pending')}
            </div>
            <TeamMemberPermissionSettings
              className="team-member-permissions-tooltip mt-3"
              value={permissions}
              defaultValue={
                member.team_permissions ? member.team_permissions.map((p) => p.name) : []
              }
              readOnly={!editing}
              writablePermissions={writablePermissions}
              isLeader={false}
              onChange={handlePermissionToggle}
              showOwnerOnlyMessage={false}
              showCannotEditOwnPermissions={false}
              isAdminTeam
              isInstitution={isHealthcareFacility}
            />
          </>
        )}
      >
        <InvitationAvatar
          user={memberUser}
          email={member.email}
          confirmed={member.confirmed}
        />
      </InvitationTooltipBase>
    </div>
  );
}

AdminMemberAvatar.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.number,
    email: PropTypes.string,
    confirmed: PropTypes.bool,
    user: PropTypes.shape(),
    team_permissions: PropTypes.arrayOf(PropTypes.shape()),
  }).isRequired,
  admin: PropTypes.bool,
  onResendInvitation: PropTypes.func.isRequired,
  onRemoveMember: PropTypes.func.isRequired,
  onPermissionsChange: PropTypes.func.isRequired,
  isHealthcareFacility: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

AdminMemberAvatar.defaultProps = {
  admin: false,
  isHealthcareFacility: false,
};

/**
 * AdminTeamForm - Form for managing admin team members
 * Uses the same visual pattern as TeamForm.jsx
 */
function AdminTeamForm(props) {
  const { adminTeam, admin } = props;

  const { t } = useTranslation();
  const { handleCatched } = useError();
  const { success, error: showError } = useToast();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePermissions, setInvitePermissions] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);

  const adminTeamMembersState = useSelector((state) => state.adminTeamMembers);

  // Filter members for this admin team
  const teamMembers = useMemo(() => {
    if (!adminTeam) return [];
    return Object.values(adminTeamMembersState).filter(
      (m) => m.team === adminTeam.id,
    );
  }, [adminTeamMembersState, adminTeam]);

  // Check if owner is a healthcare facility (for institution-only permissions)
  // eslint-disable-next-line camelcase
  const isHealthcareFacility = adminTeam?.owner_type === USER_TYPE_HEALTHCARE_FACILITY;

  // Get available permissions based on organization type
  const getAvailablePermissions = () => {
    if (isHealthcareFacility) {
      return ADMIN_TEAM_PERMISSIONS;
    }
    // Filter out institution-only permissions for non-healthcare facilities
    return ADMIN_TEAM_PERMISSIONS.filter(
      (p) => !INSTITUTION_ONLY_PERMISSIONS.includes(p),
    );
  };

  const availablePermissions = getAvailablePermissions();

  // Fetch members when component mounts
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setLoading(true);
        await dispatch(adminTeamMembersActions.list(
          { team: adminTeam.id },
          { pagination: 'no', admin },
        ));
      } catch (err) {
        handleCatched(props, err);
      } finally {
        setLoading(false);
      }
    };

    if (adminTeam) {
      fetchMembers();
    }
  }, [adminTeam?.id]);

  // Get permission choices for the invite form
  const getPermissionChoices = () => {
    const permissions = [
      {
        text: (
          <span>
            {t('user:admin-team.permissions.can_create_and_administrate_projects')}
            <Help iconClassName="ml-2 align-middle">
              {t('user:admin-team.permissions.can_create_and_administrate_projects_help')}
            </Help>
          </span>
        ),
        value: CAN_CREATE_AND_ADMINISTRATE_PROJECTS,
      },
      {
        text: (
          <span>
            {t('user:admin-team.permissions.can_view_projects')}
            <Help iconClassName="ml-2 align-middle">
              {t('user:admin-team.permissions.can_view_projects_help')}
            </Help>
          </span>
        ),
        value: CAN_VIEW_PROJECTS,
      },
      {
        text: (
          <span>
            {t('user:admin-team.permissions.can_manage_public_profile')}
            <Help iconClassName="ml-2 align-middle">
              {t('user:admin-team.permissions.can_manage_public_profile_help')}
            </Help>
          </span>
        ),
        value: CAN_MANAGE_PUBLIC_PROFILE,
      },
      {
        text: (
          <span>
            {t('user:admin-team.permissions.can_invite_team_member')}
            <Help iconClassName="ml-2 align-middle">
              {t('user:admin-team.permissions.can_invite_team_member_help')}
            </Help>
          </span>
        ),
        value: CAN_INVITE_TEAM_MEMBER,
      },
      {
        text: (
          <span>
            {t('user:admin-team.permissions.can_manage_team_permissions')}
            <Help iconClassName="ml-2 align-middle">
              {t('user:admin-team.permissions.can_manage_team_permissions_help')}
            </Help>
          </span>
        ),
        value: CAN_MANAGE_TEAM_PERMISSIONS,
      },
    ];

    // Add institution-only permissions if applicable
    if (isHealthcareFacility) {
      permissions.splice(1, 0, {
        text: (
          <span>
            {t('user:admin-team.permissions.can_create_and_administrate_research_teams')}
            <Help iconClassName="ml-2 align-middle">
              {t('user:admin-team.permissions.can_create_and_administrate_research_teams_help')}
            </Help>
          </span>
        ),
        value: CAN_CREATE_AND_ADMINISTRATE_RESEARCH_TEAMS,
      });

      permissions.splice(3, 0, {
        text: (
          <span>
            {t('user:admin-team.permissions.can_view_research_teams')}
            <Help iconClassName="ml-2 align-middle">
              {t('user:admin-team.permissions.can_view_research_teams_help')}
            </Help>
          </span>
        ),
        value: CAN_VIEW_RESEARCH_TEAMS,
      });
    }

    return permissions;
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();

    if (!inviteEmail.trim()) {
      showError(t('error:validation.email_required'));
      return;
    }

    if (invitePermissions.length === 0) {
      showError(t('error:validation.permissions_required'));
      return;
    }

    try {
      setInviteLoading(true);
      const newMember = await api.create(
        `admin-teams/${adminTeam.id}/add-member`,
        {
          email: inviteEmail.trim(),
          permissions: invitePermissions,
        },
        { admin },
      );
      // Add the new member to Redux store
      dispatch(adminTeamMembersActions.createSuccess(newMember));
      success(t('error:valid.success'));
      setInviteEmail('');
      setInvitePermissions([]);
      setShowInviteForm(false);
      // No need to call onUpdate() - Redux store is already updated
    } catch (err) {
      handleCatched(props, err);
    } finally {
      setInviteLoading(false);
    }
  };

  const handlePermissionChange = (e) => {
    setInvitePermissions(eventToMultipleChoicesValue(e, invitePermissions));
  };

  const handleResendInvitation = async (member) => {
    try {
      const memberPerms = member.team_permissions
        ? member.team_permissions.map((p) => p.name)
        : [];
      await api.create(
        `admin-teams/${adminTeam.id}/add-member`,
        {
          email: member.email,
          permissions: memberPerms,
        },
        { admin },
      );
      success(t('user:admin-team.member.invitation-resent'));
    } catch (err) {
      handleCatched(props, err);
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      // Use Redux action to delete and update store automatically
      await dispatch(adminTeamMembersActions.remove(memberId, { admin }));
      success(t('error:valid.success'));
      // No need to call onUpdate() - Redux store is already updated
    } catch (err) {
      handleCatched(props, err);
    }
  };

  const handlePermissionsChange = async (member, newPermissions) => {
    try {
      await api.create(
        `admin-teams/${adminTeam.id}/add-member`,
        {
          email: member.email,
          permissions: newPermissions,
        },
        { admin },
      );
      success(t('error:valid.success'));
      // Don't call onUpdate() here - local state is already updated
      // and reloading would cause unnecessary component refresh
    } catch (err) {
      handleCatched(props, err);
    }
  };

  // Filter out owner-only members (the organization itself)
  const regularMembers = teamMembers.filter((m) => m.type !== 'owner_only');
  // Separate leaders from regular members
  const leaderMembers = regularMembers.filter((m) => m.leader);
  const nonLeaderMembers = regularMembers.filter((m) => !m.leader);

  return (
    <div className="new-settings mx-4">
      {loading && <CardLoader />}

      {/* Organization Section - Same layout as Invitations in TeamForm */}
      <RowContent
        label={t('user:admin-team.organization')}
        contentClassName="col-12 col-md-8 col-lg-7 col-xl-7 mt-1"
      >
        {/* Chef(s) d'équipe subsection */}
        <div className="mb-3">
          <Label className="mb-2">
            {t('user:team.invitations.leaders')}
          </Label>
          <div className="row">
            <div className="col-12 col-sm">
              {/* Organization owner is always shown as a leader */}
              <div className="mr-2 mt-2 position-relative d-inline">
                <UserAvatar
                  user={{
                    id: adminTeam?.owner?.id || adminTeam?.owner,
                    // eslint-disable-next-line camelcase
                    label: adminTeam?.owner_display_name,
                    // eslint-disable-next-line camelcase
                    type: adminTeam?.owner_type,
                  }}
                />
              </div>
              {/* Team member leaders */}
              {leaderMembers.map((member) => (
                <AdminMemberAvatar
                  key={member.id}
                  member={member}
                  adminTeam={adminTeam}
                  admin={admin}
                  onResendInvitation={handleResendInvitation}
                  onRemoveMember={handleRemoveMember}
                  onPermissionsChange={handlePermissionsChange}
                  isHealthcareFacility={isHealthcareFacility}
                  t={t}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Membre(s) subsection */}
        <div className="mb-3">
          <Label className="mb-2">
            {t('user:team.invitations.members')}
          </Label>
          <div className="row">
            <div className="col-12 col-sm">
              {nonLeaderMembers.length > 0 ? (
                nonLeaderMembers.map((member) => (
                  <AdminMemberAvatar
                    key={member.id}
                    member={member}
                    adminTeam={adminTeam}
                    admin={admin}
                    onResendInvitation={handleResendInvitation}
                    onRemoveMember={handleRemoveMember}
                    onPermissionsChange={handlePermissionsChange}
                    isHealthcareFacility={isHealthcareFacility}
                    t={t}
                  />
                ))
              ) : (
                <div className="font-italic text-gray pt-1">
                  {t('user:team.invitations.none')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add Member Button */}
        {!showInviteForm && (
          <div className="pt-3">
            <NewTooltip
              content={t('user:admin-team.new-invitation.title-tooltip')}
              containerClassName="team-new-invitation-tooltip"
              placement="right"
              arrow
            >
              <button
                type="button"
                className="btn btn-invite-in-a-team"
                onClick={() => {
                  const defaultPerms = [CAN_VIEW_PROJECTS];
                  if (isHealthcareFacility) {
                    defaultPerms.push(CAN_VIEW_RESEARCH_TEAMS);
                  }
                  setInvitePermissions(defaultPerms);
                  setShowInviteForm(true);
                }}
              >
                {t('user:admin-team.add-member')}
              </button>
            </NewTooltip>
          </div>
        )}

        {/* Invite Form - Same layout as TeamForm */}
        {showInviteForm && (
          <div className="col-12 mt-4">
            <div className="mb-3">
              <Label>
                <span>
                  {t('user:admin-team.new-invitation.email')}
                  <Help iconClassName="ml-2">
                    {t('user:admin-team.new-invitation.title-tooltip')}
                  </Help>
                </span>
              </Label>
              <input
                type="email"
                className="form-control"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('user:admin-team.new-invitation.email-placeholder')}
                required
              />
            </div>

            <div className="mb-3">
              <Label className="mb-2">
                {t('user:admin-team.new-invitation.permissions')}
              </Label>
              <LabeledChoice
                rowClassName="row text-left d-block"
                colLabelClassName="col-12"
                colInputClassName="col-12 text-left"
                multipleChoices
                orientation="vertical"
                hideOptionalLabel
                hideLabel
                name="invite-permissions"
                value={invitePermissions}
                choices={getPermissionChoices()}
                disabledChoices={
                  ADMIN_TEAM_PERMISSIONS.filter((p) => !availablePermissions.includes(p))
                }
                onChange={handlePermissionChange}
              />
            </div>

            <button
              type="button"
              className={`btn btn-invite-in-a-team text-white mt-1 ${inviteEmail.trim() && invitePermissions.length > 0 ? '' : 'cursor-default'}`}
              disabled={!inviteEmail.trim() || invitePermissions.length === 0}
              onClick={handleInviteSubmit}
            >
              <span>{t('common:button.invite')}</span>
              {inviteLoading && (
                <div className="d-inline-block ml-1">
                  <CardLoader />
                </div>
              )}
            </button>
            <button
              type="button"
              className="ml-4 btn btn-link"
              disabled={inviteLoading}
              onClick={() => {
                setShowInviteForm(false);
                setInviteEmail('');
                setInvitePermissions([]);
              }}
            >
              {t('common:button.cancel')}
            </button>
          </div>
        )}
      </RowContent>

      {/* Help Section */}
      <RowContent
        label={t('user:admin-team.help.title')}
        contentClassName="col-12 col-md-8 col-lg-7 col-xl-7 mt-2"
      >
        <div className="alert alert-info mb-0">
          <p className="mb-2">
            <strong>{t('user:admin-team.help.description')}</strong>
          </p>
          <p className="mb-1">{t('user:admin-team.help.members-can')}</p>
          <ul className="mb-0">
            {t('user:admin-team.help.members-list', { returnObjects: true }).map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`help-item-${index}`}>{item}</li>
            ))}
          </ul>
        </div>
      </RowContent>
    </div>
  );
}

AdminTeamForm.propTypes = {
  adminTeam: PropTypes.shape({
    id: PropTypes.number,
    owner: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    owner_display_name: PropTypes.string,
    owner_email: PropTypes.string,
    owner_type: PropTypes.string,
    name: PropTypes.string,
  }),
  admin: PropTypes.bool,
};

AdminTeamForm.defaultProps = {
  adminTeam: null,
  admin: false,
};

export default AdminTeamForm;
